import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TvCard from '@/components/base/TvCard';
import { tvShows, categories } from '@/mocks/tvShows';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSlug]);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];

  let filteredShows = activeSlug === 'all'
    ? [...tvShows]
    : tvShows.filter((s) => s.categorySlug === activeSlug);

  if (sortBy === 'hot') {
    filteredShows.sort((a, b) => (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0));
  } else if (sortBy === 'new') {
    filteredShows.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
  } else if (sortBy === 'rating') {
    filteredShows.sort((a, b) => b.rating - a.rating);
  }

  const pageTitle = activeSlug === 'all'
    ? '全部电视剧 - 夫妻影院'
    : `${currentCategory.name}全集在线观看 - 夫妻影院`;
  const pageDesc = activeSlug === 'all'
    ? '夫妻影院提供海量电视剧全集在线观看，涵盖国产剧、韩剧、美剧、日剧、动漫等分类，最新热门电视剧免费高清不缓冲秒播流畅。'
    : `夫妻影院提供${currentCategory.name}全集免费在线观看，最新热门${currentCategory.name}高清不卡顿，秒播流畅不缓冲。每日更新最新${currentCategory.name}资源。`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '夫妻影院首页',
        item: 'https://www.fuqicinema.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: pageTitle,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: pageTitle,
            description: pageDesc,
            url: `https://www.fuqicinema.com/category/${activeSlug}`,
          }),
        }}
      />

      <Navbar />
      <main className="pt-20 md:pt-24 pb-10">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
              首页
            </Link>
            <span>/</span>
            <span className="text-foreground-300">{currentCategory.name}</span>
          </nav>

          <div className="mb-6">
            <h1 className="text-xl md:text-3xl font-bold text-foreground-100 font-heading mb-2">
              {activeSlug === 'all' ? '全部电视剧' : currentCategory.name}
            </h1>
            <p className="text-xs md:text-sm text-foreground-500">
              共 {filteredShows.length} 部电视剧
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-accent-500/20 text-accent-500 border border-accent-500/40 font-medium'
                      : 'bg-background-200/50 text-foreground-400 border border-background-300/40 hover:text-foreground-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-1 bg-background-200/50 rounded-full p-1">
              {(['hot', 'new', 'rating'] as const).map((key) => (
                <button
                  key={key}
                  onClick={() => setSortBy(key)}
                  className={`px-3 py-1.5 rounded-full text-xs transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === key
                      ? 'bg-accent-500 text-background-950 font-medium'
                      : 'text-foreground-500 hover:text-foreground-300'
                  }`}
                >
                  {key === 'hot' ? '最热' : key === 'new' ? '最新' : '评分'}
                </button>
              ))}
            </div>
          </div>

          {filteredShows.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-5">
              {filteredShows.map((show) => (
                <TvCard key={show.id} show={show} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-foreground-600 text-3xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无电视剧，请浏览其他分类</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}